import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/cabanas/cabana-hero.jpg";
import includedImg from "@/assets/cabanas/whats-included.jpg";
import grillImg from "@/assets/cabanas/cabana-grill-seating.jpg";
import familyImg from "@/assets/cabanas/family-group-cabana.webp";

const PUBLISH_DATE = "May 5, 2026";
const CANONICAL = "/blog/cabana-rentals-price-whats-included";
const TITLE =
  "Guadalupe Cabana Rentals — Price & What's Included";
const DESCRIPTION =
  "Cabana rentals on the Guadalupe River near New Braunfels from $69. See pricing, what's included — picnic table, parking, river access — and how to book.";

const FAQS = [
  {
    q: "How much does a cabana rental on the Guadalupe River cost?",
    a: "Cabana rentals at Son's Guadalupe start at $69 on weekdays, with seasonal weekend and holiday pricing. We also run promos like Sunday Funday with $69 cabanas all day.",
  },
  {
    q: "What's included with a cabana rental?",
    a: "Every cabana includes space for up to 6 guests, 1 free parking spot, a picnic table, direct river access, a shaded covered structure on the Guadalupe River, and access to on-site restrooms.",
  },
  {
    q: "How many people fit in one cabana?",
    a: "Each cabana is built for up to 6 guests. Larger groups can book multiple cabanas side by side or call our team for help setting up a group reservation.",
  },
  {
    q: "Are tubes and kayaks included with the cabana?",
    a: "No — tubes and kayaks are separate add-ons. All-day tube rentals start at $29.99 and kayak rentals are available on most properties.",
  },
  {
    q: "Can I park more than one car at my cabana?",
    a: "Yes. Each cabana includes 1 free parking spot, and additional vehicles are available for a small per-car fee.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Cabana Rentals on the Guadalupe River — Price & What's Included", item: `https://sonsguadalupe.com/blog/cabana-rentals-price-whats-included` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Cabana Rentals on the Guadalupe River — Price & What's Included",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-05",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "cabana rentals near New Braunfels, cabana rentals on the Guadalupe River, cabana price, what's included with a cabana rental, tubing the Guadalupe River",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogCabanaPriceIncluded = () => {
  const related = getRelatedPosts("cabana-rentals-price-whats-included", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img src={heroImage} alt="Riverside cabana on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Cabana Rentals on the Guadalupe River — Price & What's Included
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Wondering what <strong>cabana rentals on the Guadalupe River</strong> actually cost — and what you get for the money? Here's the simple breakdown of <strong>cabana rentals near New Braunfels</strong> at Son's Guadalupe: pricing, inclusions, group size, parking, and how to lock in your spot.
              </p>

              <img src={includedImg} alt="What's included with a cabana rental on the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How Much Does a Cabana Rental Cost?</h2>
              <p>
                <strong>Cabanas start at $69</strong> on weekdays, with seasonal weekend and holiday pricing. We frequently run promos like <em>Sunday Funday</em> with $69 cabanas all day — check the banner at the top of the site for current deals. Pricing varies by property, day of week, and season.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What's Included With Every Cabana Rental?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Up to 6 guests</strong> per cabana</li>
                <li><strong>1 free parking spot</strong> per cabana</li>
                <li><strong>Picnic table</strong> at your cabana</li>
                <li><strong>Direct river access</strong> from your spot — float right from the cabana</li>
                <li>Shaded covered structure on the Guadalupe River</li>
                <li>Access to on-site restrooms and friendly staff</li>
                <li>A calm, family-friendly atmosphere</li>
              </ul>

              <img src={grillImg} alt="Cabana with grill and seating area on the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What's NOT Included (and Common Add-Ons)</h2>
              <p>
                A cabana rental is your basecamp — most guests add tubes, kayaks, or extras. Here's what to think about:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Tube rental</strong> — starting at $29.99 for all-day tubing</li>
                <li><strong>Kayak rental</strong> — available on most properties</li>
                <li><strong>Extra parking</strong> — additional vehicles are a small per-car fee</li>
                <li><strong>Food & drinks</strong> — bring your own coolers (no glass)</li>
                <li><strong>Overnight stays</strong> — pair with glamping near New Braunfels or tent camping on River Road</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How Many People Fit in a Cabana?</h2>
              <p>
                Each cabana is built for <strong>up to 6 guests</strong>. Bigger group? Book multiple cabanas side by side, or call our team and we'll help you set up a group reservation across our properties. We host birthday parties, bachelorette weekends, family reunions, and corporate days all the time.
              </p>

              <img src={familyImg} alt="Family relaxing at a Son's Guadalupe cabana on the river" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Is a Cabana Worth It?</h2>
              <p>
                If you're tubing the Guadalupe River with a family or any group of 4+, the answer is almost always yes. Compared to fighting for a spot at a public park, paying for a paid lot, and hauling coolers a half-mile, a cabana gives you a private shaded basecamp on the water for the entire day. <strong>Cabana rentals near New Braunfels</strong> are the easiest way to do a Guadalupe River day right.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How to Book Your Cabana</h2>
              <p>
                Pick your date and property on our Book Now page, choose your cabana, and add tubes or kayaks if you want them. Cabanas book out fast on weekends and holidays — reserve early, especially for summer and spring break.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Cabana Rental FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book a Cabana</Button></Link>
                <Link to="/cabanas"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Cabana Details</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More Cabana Resources"
        heading="Build Your Day Around a Cabana on the Guadalupe"
        paragraphs={[
          "A cabana rental on the Guadalupe River is the perfect basecamp for tubing, kayaking, swimming, and family getaways near New Braunfels. Add glamping near New Braunfels or tent camping on River Road to turn a day into a full weekend.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogCabanaPriceIncluded;
